import React from 'react';
import ResourceModal from './ResourceModal';
import { Separator } from "@/components/ui/separator";
import { FaBookOpen, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

interface MediaLiteracy101Props {
  onClose: () => void;
}

const MediaLiteracy101: React.FC<MediaLiteracy101Props> = ({ onClose }) => {
  return (
    <ResourceModal title="Media Literacy 101" onClose={onClose}>
      <div className="space-y-6">
        <div className="flex items-center gap-4 bg-truth/10 rounded-xl p-4">
          <div className="bg-truth/20 p-3 rounded-full">
            <FaBookOpen className="text-truth h-8 w-8" />
          </div>
          <div>
            <h3 className="text-xl font-bold">Course Overview</h3>
            <p className="text-gray-400">A comprehensive introduction to media literacy skills for the digital age</p>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold mb-2">What is Media Literacy?</h3>
          <p className="text-gray-300 mb-4">
            Media literacy is the ability to access, analyze, evaluate, create, and act using all forms of communication. 
            In its simplest terms, it's the ability to think critically about the media we consume and create.
          </p>
          <p className="text-gray-300">
            In today's digital landscape, being media literate is more important than ever. We're constantly bombarded with 
            information from countless sources, and distinguishing fact from fiction has become increasingly challenging.
          </p>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-2">Core Concepts</h3>
          <ul className="space-y-4">
            {[
              {
                title: "All Media is Constructed",
                description: "Media messages are created by individuals who make conscious and unconscious choices about what to include, what to leave out, and how to present the information."
              },
              {
                title: "Media Uses a Creative Language with Its Own Rules",
                description: "Each form of media has its own unique set of principles, techniques, and aesthetics that impact how information is presented and interpreted."
              },
              {
                title: "Different People Experience the Same Media Differently",
                description: "People's experiences, knowledge, beliefs, and values affect how they interpret and respond to media."
              },
              {
                title: "Media Has Embedded Values and Points of View",
                description: "Media is never neutral; it always presents information from a certain perspective and with certain values."
              },
              {
                title: "Media is Organized to Gain Profit and/or Power",
                description: "Understanding who owns, creates, and funds different media can help understand potential biases and motives."
              }
            ].map((concept, index) => (
              <li key={index} className="bg-black/20 border border-gray-800 rounded-lg p-4">
                <h4 className="font-semibold text-truth mb-1">{concept.title}</h4>
                <p className="text-gray-300 text-sm">{concept.description}</p>
              </li>
            ))}
          </ul>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-2">Key Skills to Develop</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Source Evaluation",
                description: "Assessing the credibility and reliability of information sources."
              },
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Content Analysis",
                description: "Breaking down messages to understand their construction and purpose."
              },
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Context Consideration",
                description: "Understanding how timing, placement, and framing affect meaning."
              },
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Critical Thinking",
                description: "Questioning, analyzing, and evaluating media messages before accepting them."
              },
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Message Creation",
                description: "Creating responsible and ethical media content."
              },
              {
                icon: <FaCheckCircle className="h-5 w-5 text-green-500" />,
                skill: "Digital Citizenship",
                description: "Engaging with media in ways that promote democracy, equality, and social responsibility."
              }
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                <div className="mt-1">{item.icon}</div>
                <div>
                  <h4 className="font-medium">{item.skill}</h4>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-red-900/20 border border-red-900/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <FaExclamationTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-red-400 mb-1">Why This Matters</h4>
              <p className="text-gray-300 text-sm">
                The spread of misinformation and disinformation poses significant threats to democracies, public health, and social cohesion.
                Media literacy is our best defense against manipulation and our strongest tool for making informed decisions in a complex media landscape.
              </p>
            </div>
          </div>
        </div>

        <Separator />

        <div>
          <h3 className="text-lg font-bold mb-2">Course Modules</h3>
          <ol className="space-y-3">
            {[
              "Introduction to Media Literacy Concepts",
              "Understanding News Media Structures and Biases",
              "Digital Media and Social Networks",
              "Visual Literacy and Image Manipulation",
              "Advertising and Commercial Media Analysis",
              "Media Creation Ethics and Responsibilities",
              "Advanced Fact-Checking Techniques",
              "Media Literacy in Practice: Case Studies"
            ].map((module, index) => (
              <li key={index} className="flex gap-3">
                <span className="bg-truth/20 text-truth h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-gray-300">{module}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </ResourceModal>
  );
};

export default MediaLiteracy101;
